// src/utils/normalizeQso.ts
import { type QsoType } from '@/types';
import { trimObjectValues } from './formatters';
import {
  UPPERCASE_NORMALIZATION_FIELDS,
  LOWERCASE_NORMALIZATION_FIELDS,
  STRING_COERCION_FIELDS,
} from '@/constants/normalizationFields';

// Helper function to validate the qsoDate and timeOn
function isValidDateTime(qsoDate: string | undefined, timeOn: string | undefined): boolean {
  if (!qsoDate || !timeOn) {
    return false;
  }

  const dateTimeString = `${qsoDate}T${timeOn}Z`;
  const date = new Date(dateTimeString);

  return !Number.isNaN(date.getTime());
}

/**
 * Read-boundary helper. Walks `STRING_COERCION_FIELDS` and replaces any
 * non-string value with its string form. Used both inside `normalizeQso`
 * (write path) and from `QsoPouchRepo` read methods (to clean up legacy
 * records persisted with non-string values before the XML parser was fixed).
 *
 * Returns the same object reference if no fields needed coercion (cheap
 * fast-path for the common case where data is already clean).
 *
 * Intentionally permanent — see the rationale on `STRING_COERCION_FIELDS`
 * for why we don't backfill via a migration.
 */
export const coerceQsoTextFields = <T extends Partial<QsoType>>(qso: T): T => {
  let mutated: T | null = null;
  for (const field of STRING_COERCION_FIELDS) {
    const value = qso[field];
    if (value === undefined || value === null || typeof value === 'string') continue;
    if (!mutated) mutated = { ...qso };
    (mutated as Record<string, unknown>)[field] = String(value);
  }
  return mutated ?? qso;
};

// Function to normalize the QSO object
// Uses shared normalization rules from constants/normalizationFields.ts
export const normalizeQso = (qso: Partial<QsoType>): QsoType => {
  qso = trimObjectValues(qso);

  const normalizedQso: Record<string, any> = {
    ...qso,
    ...(isValidDateTime(qso.qsoDate, qso.timeOn) && {
      qsoDateTime: new Date(`${qso.qsoDate}T${qso.timeOn}Z`).toISOString(),
    }),
  };

  // Uppercase relevant fields
  // Coerce to string first (like createDupeKey) for consistency
  UPPERCASE_NORMALIZATION_FIELDS.forEach((field) => {
    const fieldValue = qso[field];
    if (fieldValue !== undefined && fieldValue !== null) {
      normalizedQso[field] = String(fieldValue).trim().toUpperCase();
    }
  });

  // Lowercase relevant fields
  // Coerce to string first (like createDupeKey) for consistency
  LOWERCASE_NORMALIZATION_FIELDS.forEach((field) => {
    const fieldValue = qso[field];
    if (fieldValue !== undefined && fieldValue !== null) {
      normalizedQso[field] = String(fieldValue).trim().toLowerCase();
    }
  });

  // Coerce text fields to strings (defensive against legacy non-string values).
  // Single source of truth — same helper that runs at the read boundary.
  return coerceQsoTextFields(normalizedQso as QsoType);
};